'use client';

import React, { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import Link from 'next/link';
import {
  BookOpen,
  Play,
  Clock,
  ChevronRight,
  Search,
  Wind,
  Heart,
  Shield,
  Stethoscope,
  Star,
  CheckCircle2,
  Video,
} from 'lucide-react';

type Category = 'All' | 'COPD' | 'Asthma' | 'TB' | 'General';

interface Article {
  id: number;
  title: string;
  summary: string;
  category: Category;
  readTime: string;
  type: 'article' | 'video' | 'guide';
  featured?: boolean;
}

interface PatientVideo {
  id: string;
  day_number: number;
  title: string;
  category: string;
  description: string;
  video_url: string;
  thumbnail_url: string | null;
  duration: string | null;
  file_size: number | null;
  created_at: string;
}

interface Tip {
  icon: React.ElementType;
  color: string;
  bg: string;
  title: string;
  desc: string;
}

const categories: Category[] = ['All', 'COPD', 'Asthma', 'TB', 'General'];

const articles: Article[] = [
  { id: 1, title: 'Understanding COPD: What You Need to Know', summary: 'Learn about Chronic Obstructive Pulmonary Disease — its causes, stages, and how to manage daily life with COPD.', category: 'COPD', readTime: '5 min read', type: 'article', featured: true },
  { id: 2, title: 'How to Use Your Inhaler Correctly', summary: 'Step-by-step guide to using MDI and DPI inhalers for maximum medication delivery to your lungs.', category: 'General', readTime: '3 min read', type: 'guide' },
  { id: 3, title: 'Pursed Lip Breathing Technique', summary: 'A simple breathing exercise that helps slow breathing, reduces breathlessness, and improves oxygen exchange.', category: 'COPD', readTime: '4 min video', type: 'video' },
  { id: 4, title: 'Asthma Triggers: Identify & Avoid', summary: 'Common asthma triggers in Jammu — dust, smoke, cold air, and seasonal allergens — and how to minimize exposure.', category: 'Asthma', readTime: '4 min read', type: 'article' },
  { id: 5, title: 'Tuberculosis Treatment: Completing Your DOTS Course', summary: 'Why completing the full DOTS course is critical for TB cure and preventing drug-resistant TB.', category: 'TB', readTime: '6 min read', type: 'article' },
  { id: 6, title: 'Nutrition for Lung Health', summary: 'Foods that support lung function — antioxidant-rich fruits, omega-3s, and what to avoid for better breathing.', category: 'General', readTime: '5 min read', type: 'article' },
  { id: 7, title: 'COPD & Exercise: Staying Active Safely', summary: 'How pulmonary rehabilitation and gentle exercise can improve quality of life for COPD patients.', category: 'COPD', readTime: '5 min read', type: 'guide' },
  { id: 8, title: 'Understanding Your Spirometry Results', summary: 'What FEV1, FVC, and FEV1/FVC ratio mean and how they help your doctor assess your lung function.', category: 'General', readTime: '4 min read', type: 'article' },
];

const dailyTips: Tip[] = [
  { icon: Wind, color: 'text-blue-500', bg: 'bg-blue-50', title: 'Avoid Smoke Exposure', desc: 'Stay away from wood smoke, incense, and vehicle exhaust — common triggers in Jammu.' },
  { icon: Heart, color: 'text-red-500', bg: 'bg-red-50', title: 'Pace Yourself', desc: 'Break activities into short bursts. Rest between tasks to avoid breathlessness.' },
  { icon: Shield, color: 'text-green-500', bg: 'bg-green-50', title: 'Flu Vaccination', desc: 'Annual flu shots significantly reduce COPD exacerbation risk.' },
];

const typeIcon = (type: Article['type']) => {
  if (type === 'video') return <Play size={11} className="text-white" />;
  if (type === 'guide') return <Stethoscope size={11} className="text-white" />;
  return <BookOpen size={11} className="text-white" />;
};

const typeBg = (type: Article['type']) => {
  if (type === 'video') return 'bg-red-500';
  if (type === 'guide') return 'bg-purple-500';
  return 'bg-teal-600';
};

const categoryBadgeColor = (cat: string) => {
  const map: Record<string, { bg: string; text: string }> = {
    'COPD': { bg: '#EFF6FF', text: '#1D4ED8' },
    'Asthma': { bg: '#FFF7ED', text: '#C2410C' },
    'TB': { bg: '#FEF2F2', text: '#DC2626' },
    'Allergy': { bg: '#F5F3FF', text: '#7C3AED' },
    'Breathing Exercise': { bg: '#ECFDF5', text: '#059669' },
    'Smoking Cessation': { bg: '#FFFBEB', text: '#D97706' },
    'General Lung Care': { bg: '#F0FDF4', text: '#16A34A' },
  };
  return map[cat] || { bg: '#F3F4F6', text: '#374151' };
};

export default function LungAcademyPage() {
  const router = useRouter();
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState<Category>('All');
  const [completedIds, setCompletedIds] = useState<number[]>([]);
  const [drVideos, setDrVideos] = useState<PatientVideo[]>([]);
  const [videosLoading, setVideosLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem('lungAcademyCompleted');
    if (stored) setCompletedIds(JSON.parse(stored));
  }, []);

  useEffect(() => {
    const fetchVideos = async () => {
      setVideosLoading(true);
      try {
        const { createClient } = await import('@/lib/supabase/client');
        const supabase = createClient();
        if (!supabase) { setVideosLoading(false); return; }
        const { data, error } = await supabase
          .from('patient_videos')
          .select('*')
          .order('day_number', { ascending: false });
        if (!error && data) setDrVideos(data as PatientVideo[]);
      } catch (err) {
        console.error('Failed to fetch videos:', err);
      } finally {
        setVideosLoading(false);
      }
    };
    fetchVideos();
  }, []);

  const filtered = articles.filter((a) => {
    const matchCat = activeCategory === 'All' || a.category === activeCategory;
    const matchSearch = a.title.toLowerCase().includes(search.toLowerCase()) || a.summary.toLowerCase().includes(search.toLowerCase());
    return matchCat && matchSearch;
  });

  const featured = articles.find((a) => a.featured);
  const completedCount = completedIds.length;
  const latestVideo = drVideos[0] || null;
  const previousVideos = drVideos.slice(1);

  const handleArticleClick = (article: Article) => {
    router.push(`/patient/article/${article.id}`);
  };

  return (
    <div className="max-w-lg mx-auto px-4 pt-6 space-y-6 pb-28">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Lung Academy</h1>
        <p className="text-sm text-gray-500 mt-1">Learn about your condition &amp; lung health</p>
      </div>

      {/* ─── DAILY VIDEOS SECTION ─── */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <div className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: '#A51C30' }}>
            <Video size={14} className="text-white" />
          </div>
          <h2 className="text-base font-bold text-gray-900">Daily 5-Minute Videos by Dr Neeraj</h2>
        </div>

        {videosLoading ? (
          <div className="rounded-2xl p-8 flex items-center justify-center" style={{ background: '#fff', border: '1px solid #F3F4F6' }}>
            <div className="w-6 h-6 rounded-full border-2 border-t-transparent animate-spin" style={{ borderColor: '#A51C30', borderTopColor: 'transparent' }} />
          </div>
        ) : latestVideo ? (
          <>
            {/* Latest Video — Large Player */}
            <div className="rounded-2xl overflow-hidden mb-4" style={{ background: '#111827', boxShadow: '0 4px 20px rgba(0,0,0,0.15)' }}>
              <video
                src={latestVideo.video_url}
                controls
                preload="metadata"
                playsInline
                className="w-full"
                style={{ maxHeight: '240px', background: '#000' }}
                poster={latestVideo.thumbnail_url || undefined}
              />
              <div className="p-4">
                <div className="flex items-center gap-2 mb-1.5">
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full" style={{ background: '#A51C30', color: '#fff' }}>Day {latestVideo.day_number}</span>
                  <span className="text-xs font-semibold px-2 py-0.5 rounded-full" style={categoryBadgeColor(latestVideo.category)}>{latestVideo.category}</span>
                  <span className="text-xs font-bold px-2 py-0.5 rounded-full ml-auto" style={{ background: '#ECFDF5', color: '#059669' }}>Latest</span>
                </div>
                <p className="text-sm font-bold text-white leading-snug">{latestVideo.title}</p>
                {latestVideo.description && <p className="text-xs mt-1 leading-relaxed" style={{ color: '#9CA3AF' }}>{latestVideo.description}</p>}
                <Link
                  href={`/patient/video/${latestVideo.id}`}
                  className="mt-3 inline-flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-lg"
                  style={{ background: '#A51C30', color: '#fff' }}
                >
                  <Play size={11} /> Watch Full Screen
                </Link>
              </div>
            </div>

            {/* Previous Videos Grid */}
            {previousVideos.length > 0 && (
              <div>
                <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wide mb-3">Previous Videos</h3>
                <div className="grid grid-cols-2 gap-3">
                  {previousVideos.map((video) => (
                    <Link
                      key={video.id}
                      href={`/patient/video/${video.id}`}
                      className="rounded-xl overflow-hidden block hover:shadow-md transition-all active:scale-[0.98]"
                      style={{ background: '#fff', border: '1px solid #F3F4F6' }}
                    >
                      {/* Thumbnail */}
                      <div className="relative w-full aspect-video flex items-center justify-center" style={{ background: '#1F2937' }}>
                        {video.thumbnail_url ? (
                          <img src={video.thumbnail_url} alt={`Thumbnail for ${video.title}`} className="w-full h-full object-cover absolute inset-0" />
                        ) : (
                          <svg className="w-8 h-8" style={{ color: '#4B5563' }} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                        )}
                        {/* Play overlay */}
                        <div className="absolute inset-0 flex items-center justify-center">
                          <div className="w-9 h-9 rounded-full flex items-center justify-center" style={{ background: 'rgba(165,28,48,0.85)' }}>
                            <Play size={14} className="text-white ml-0.5" />
                          </div>
                        </div>
                        <div className="absolute top-1.5 left-1.5">
                          <span className="text-xs font-bold px-1.5 py-0.5 rounded-md" style={{ background: 'rgba(0,0,0,0.7)', color: '#fff' }}>Day {video.day_number}</span>
                        </div>
                      </div>
                      <div className="p-2.5">
                        <p className="text-xs font-bold text-gray-900 leading-snug line-clamp-2">{video.title}</p>
                        <div className="flex items-center gap-1.5 mt-1">
                          <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full" style={categoryBadgeColor(video.category)}>{video.category}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="rounded-2xl p-8 text-center" style={{ background: '#fff', border: '1px solid #F3F4F6' }}>
            <Video size={32} className="mx-auto mb-2 text-gray-300" />
            <p className="text-sm font-semibold text-gray-500">No videos yet</p>
            <p className="text-xs text-gray-400 mt-1">Dr Neeraj will upload daily education videos here.</p>
          </div>
        )}
      </div>

      {/* Progress Banner */}
      <div className="bg-teal-600 rounded-2xl p-4 text-white">
        <div className="flex items-center justify-between mb-2">
          <p className="text-xs font-semibold text-white/70 uppercase tracking-wide">Your Progress</p>
          <span className="text-xs font-bold text-white">{completedCount}/{articles.length} completed</span>
        </div>
        <div className="w-full bg-white/20 rounded-full h-2 mb-3">
          <div className="bg-white rounded-full h-2 transition-all duration-500" style={{ width: `${(completedCount / articles.length) * 100}%` }} />
        </div>
        <p className="text-sm text-white/80">Keep learning to better manage your lung health.</p>
      </div>

      {/* Featured Article */}
      {featured && (
        <div className="bg-white border border-teal-200 rounded-2xl p-4 cursor-pointer hover:shadow-md hover:border-teal-400 transition-all active:scale-[0.99]" onClick={() => handleArticleClick(featured)}>
          <div className="flex items-center gap-2 mb-2">
            <Star size={13} className="text-yellow-500 fill-yellow-500" />
            <span className="text-[10px] font-bold text-yellow-600 uppercase tracking-wide">Featured</span>
          </div>
          <p className="text-base font-bold text-gray-900 leading-snug mb-1">{featured.title}</p>
          <p className="text-xs text-gray-500 leading-relaxed mb-3">{featured.summary}</p>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <span className={`w-5 h-5 rounded-full ${typeBg(featured.type)} flex items-center justify-center`}>{typeIcon(featured.type)}</span>
              <span className="text-xs text-gray-500">{featured.readTime}</span>
            </div>
            <div className="flex items-center gap-1">
              {completedIds.includes(featured.id) && <span className="flex items-center gap-1 text-[10px] text-green-600 font-semibold mr-2"><CheckCircle2 size={12} /> Completed</span>}
              <ChevronRight size={15} className="text-gray-400" />
            </div>
          </div>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" placeholder="Search articles, guides…" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-teal-500" />
      </div>

      {/* Category Filter */}
      <div className="flex gap-2 overflow-x-auto pb-1 scrollbar-hide">
        {categories.map((cat) => (
          <button key={cat} onClick={() => setActiveCategory(cat)} className={`shrink-0 px-4 py-1.5 rounded-full text-xs font-bold border transition-all ${activeCategory === cat ? 'bg-teal-600 text-white border-teal-600' : 'bg-white text-gray-500 border-gray-200 hover:border-teal-300'}`}>{cat}</button>
        ))}
      </div>

      {/* Articles List */}
      <div className="space-y-3">
        {filtered.length === 0 ? (
          <div className="text-center py-10 text-sm text-gray-400">No articles found</div>
        ) : (
          filtered.map((article) => (
            <div key={article.id} className="bg-white border border-gray-200 rounded-xl overflow-hidden cursor-pointer hover:border-teal-300 hover:shadow-sm transition-all active:scale-[0.99]" onClick={() => handleArticleClick(article)}>
              <div className="flex items-start gap-3 p-4">
                <div className={`w-8 h-8 rounded-lg ${typeBg(article.type)} flex items-center justify-center shrink-0 mt-0.5`}>{typeIcon(article.type)}</div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-bold text-gray-900 leading-snug">{article.title}</p>
                  <div className="flex items-center gap-2 mt-1.5">
                    <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[10px] font-semibold">{article.category}</span>
                    <span className="flex items-center gap-1 text-[10px] text-gray-400"><Clock size={10} /> {article.readTime}</span>
                  </div>
                </div>
                <div className="flex flex-col items-end gap-1 shrink-0">
                  {completedIds.includes(article.id) && <CheckCircle2 size={15} className="text-green-500" />}
                  <ChevronRight size={15} className="text-gray-400" />
                </div>
              </div>
            </div>
          ))
        )}
      </div>

      {/* Daily Tips */}
      <div>
        <h2 className="text-sm font-bold text-gray-400 uppercase tracking-wide mb-3">Daily Tips</h2>
        <div className="space-y-3">
          {dailyTips.map((tip, i) => {
            const TipIcon = tip.icon;
            return (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 flex items-start gap-3">
                <div className={`w-9 h-9 ${tip.bg} rounded-xl flex items-center justify-center shrink-0`}><TipIcon size={16} className={tip.color} /></div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{tip.title}</p>
                  <p className="text-xs text-gray-500 mt-0.5 leading-relaxed">{tip.desc}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
